'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';

// Global variable to track active countdown interval
let activeCountdownInterval: NodeJS.Timeout | null = null;
export const injectCountdownTimer = (targetDateUTC: string): (() => void) | null => {
  // Clear any existing countdown interval to prevent duplicates
  if (activeCountdownInterval) {
    clearInterval(activeCountdownInterval);
    activeCountdownInterval = null;
    console.log('Cleared existing countdown interval');
  }

  // Find the countdown element in the DOM
  const countdownElement = document.getElementById('countdown-timer');
  if (!countdownElement) {
    console.warn('Countdown timer element with id="countdown-timer" not found in DOM');
    return null;
  }

  // Helper function to remove the countdown banner
  const removeCountdownBanner = () => {
    if (activeCountdownInterval) {
      clearInterval(activeCountdownInterval);
      activeCountdownInterval = null;
    }
    countdownElement.remove();
    console.log('Removed countdown banner');
  };

  // Validate the target date
  if (!targetDateUTC || targetDateUTC.trim() === '') {
    console.warn('No target date provided, removing countdown banner');
    removeCountdownBanner();
    return null;
  }
  const targetDate = new Date(targetDateUTC);
  if (isNaN(targetDate.getTime())) {
    console.error('Invalid target date provided:', targetDateUTC);
    removeCountdownBanner();
    return null;
  }
  const updateCountdown = () => {
    // Re-check if the countdown element still exists in the DOM
    const currentCountdownElement = document.getElementById('countdown-timer');
    if (!currentCountdownElement) {
      console.warn('Countdown element was removed from DOM, cleaning up interval');
      if (activeCountdownInterval) {
        clearInterval(activeCountdownInterval);
        activeCountdownInterval = null;
      }
      return;
    }
    const now = new Date().getTime();
    const target = targetDate.getTime();
    const distance = target - now;

    // Check if the countdown has ended
    if (distance < 0) {
      // Remove the countdown banner from the DOM
      removeCountdownBanner();
      return;
    }

    // Calculate time units
    const days = Math.floor(distance / (1000 * 60 * 60 * 24));
    const hours = Math.floor(distance % (1000 * 60 * 60 * 24) / (1000 * 60 * 60));
    const minutes = Math.floor(distance % (1000 * 60 * 60) / (1000 * 60));
    const seconds = Math.floor(distance % (1000 * 60) / 1000);

    // Find time unit elements by their data attributes or structure
    const daysElement = currentCountdownElement.querySelector('[data-time-unit="days"] span') || currentCountdownElement.querySelector('div:nth-child(2) > div:nth-child(1) span');
    const hoursElement = currentCountdownElement.querySelector('[data-time-unit="hours"] span') || currentCountdownElement.querySelector('div:nth-child(2) > div:nth-child(2) span');
    const minutesElement = currentCountdownElement.querySelector('[data-time-unit="minutes"] span') || currentCountdownElement.querySelector('div:nth-child(2) > div:nth-child(3) span');
    const secondsElement = currentCountdownElement.querySelector('[data-time-unit="seconds"] span') || currentCountdownElement.querySelector('div:nth-child(2) > div:nth-child(4) span');

    // Helper function to format time with leading zero
    const formatTime = (time: number) => String(time).padStart(2, '0');

    // Hide/show time units based on remaining time
    const totalHours = days * 24 + hours;

    // Find parent containers to hide/show
    const daysContainer = currentCountdownElement.querySelector('[data-time-unit="days"]') || currentCountdownElement.querySelector('div:nth-child(2) > div:nth-child(1)');
    const hoursContainer = currentCountdownElement.querySelector('[data-time-unit="hours"]') || currentCountdownElement.querySelector('div:nth-child(2) > div:nth-child(2)');
    if (totalHours < 1) {
      // Less than 1 hour remaining - hide days and hours, show only minutes and seconds
      if (daysContainer) (daysContainer as HTMLElement).style.display = 'none';
      if (hoursContainer) (hoursContainer as HTMLElement).style.display = 'none';

      // Update minutes and seconds
      if (minutesElement) minutesElement.textContent = formatTime(minutes);
      if (secondsElement) secondsElement.textContent = formatTime(seconds);
    } else {
      // More than 1 hour remaining - show all units
      if (daysContainer) (daysContainer as HTMLElement).style.display = 'flex';
      if (hoursContainer) (hoursContainer as HTMLElement).style.display = 'flex';

      // Update all time units
      if (daysElement) daysElement.textContent = formatTime(days);
      if (hoursElement) hoursElement.textContent = formatTime(hours);
      if (minutesElement) minutesElement.textContent = formatTime(minutes);
      if (secondsElement) secondsElement.textContent = formatTime(seconds);
    }

    // Show the countdown after first update (reveals the hidden countdown with actual values)
    if ((currentCountdownElement as HTMLElement).style.opacity === '0' || (currentCountdownElement as HTMLElement).style.opacity === '') {
      (currentCountdownElement as HTMLElement).style.opacity = '1';
      (currentCountdownElement as HTMLElement).style.visibility = 'visible';
      (currentCountdownElement as HTMLElement).style.transition = 'opacity 0.3s ease-in-out';
    }
  };

  // Start the countdown immediately
  updateCountdown();

  // Set up interval to update every second
  activeCountdownInterval = setInterval(updateCountdown, 1000);

  // Return cleanup function to clear the interval
  return () => {
    if (activeCountdownInterval) {
      clearInterval(activeCountdownInterval);
      activeCountdownInterval = null;
    }
  };
};
export const CountdownInjector: React.FC<{
  targetDateUTC: string;
}> = t0 => {
  const $ = _c(3);
  const {
    targetDateUTC
  } = t0;
  let t1;
  let t2;
  if ($[0] !== targetDateUTC) {
    t1 = () => {
      const cleanup = injectCountdownTimer(targetDateUTC);
      return cleanup || undefined;
    };
    t2 = [targetDateUTC];
    $[0] = targetDateUTC;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  return null;
};